<script setup>
import {ref, onMounted, watch, computed, getCurrentInstance,  defineEmits, defineProps } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from "../../../../user/stores/user";
import { usePageLoadingStore } from "../../../../../stores/pageLoading";
import mypageJobFairService from "../../../../user/services/mypage/jobFair/mypageJobFairService";
import mypageJobFairJobNoticeService from "../../../../user/services/mypage/jobFair/mypageJobFairJobNoticeService";
import JobFairCorpInfoDetailModal from '../../../../user/components/modal/jobFair/JobFairCorpInfoDetailModal.vue'
import JobFairJobNoticeDetailModal from "../../../../user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue";
import adminJobfairStateService from "../../../services/jobfair/adminJobfairStateService";

const route = useRoute()
const router = useRouter()
const loadingStore = usePageLoadingStore()

const { user, isAuthenticated } = storeToRefs(useUserStore());
const globals = getCurrentInstance().appContext.config.globalProperties;
const { $pageListBoardNo, alert, confirm } = globals;

const props = defineProps({
  corpId: {
    type: [String, Number],
    default: null,
  },
  userId: {
    type: [String, Number],
    default: null,
  }
});

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const totalCount = ref(0);
const list = ref([]); // 관심 기업 리스트
const corpDetail = ref({});

const corpInfoModal = ref(null);
const jobNoticeModal = ref(null); // 기업정보

const pageSizeGroup = ref([
  { label:"10개씩 보기", value: 10 },
  { label:"30개씩 보기", value: 30 },
  { label:"50개씩 보기", value: 50 },
  { label:"100개씩 보기", value: 100 },
])


const onSelectPage = (pageNo) => {
  currentPage.value = pageNo;
  retrieveList();
}

// 페이지당 개수 변경
const onSelectPageSize = (option) => {
  pageSize.value = option.value;
  currentPage.value = 1;
  retrieveList();
}

const retrieveCorpDetail = (corpId) => {
  loadingStore.loading = true;
  mypageJobFairService.retrieveCorporation(corpId)
    .then( (res) => {
      if(res.data.code.toLowerCase() === 'success') {
        const result = res.data.result;
        corpDetail.value = result;
      }
      loadingStore.loading = false;
    })
    .catch( (error) =>  {
      loadingStore.loading = false;
    });
}

const retrieveList = () => {
  let paramObj = {
    corpId: props.corpId,
    userId: props.userId,
    pageNumber: currentPage.value || 0,
    size: pageSize.value || 10,
  }

  loadingStore.loading = true;
  adminJobfairStateService.retrieveJobNoticeListPagingByUserId(paramObj)
    .then( (res) => {
      if(res.data.code.toLowerCase() === 'success') {
        const result = res.data.result;
        // console.log(result);
        totalPages.value = result.totalPages;
        totalCount.value = result.totalElements;
        list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
      }
      loadingStore.loading = false;
    })
    .catch( (error) =>  {
      loadingStore.loading = false;
    });
}

// 기업정보 보기
const onClickCorp = async () => {
  const result = await corpInfoModal.value.show({
    corpId: corpDetail.value.corpId,
    interestYn: corpDetail.value.interestYn,
  });
}

// 채용공고 보기
const onClickJobNotice = async (jobNotice) => {
  await jobNoticeModal.value.show({
    jobNoticeSeq: jobNotice.jobNoticeSeq,
    corpId: jobNotice.corpId,
  });
}

const openDetailInNewTab = (linkName, id = {}) => {
  const url = router.resolve({
    name: linkName,
    params: { id },
  }).href;

  window.open(url, '_blank');
}

const emit = defineEmits(['go-back']);
const goBack = () => {
  emit('go-back');
}

onMounted(() => {
  retrieveList();
  retrieveCorpDetail(props.corpId);
})
</script>

<template>
  <div class="table-form">
    <div class="list-top mb-n-xs">
      <KSASecondaryButton text="이전" @click="goBack()" />
    </div>
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <div class="flex-wrap">
        <KSAPrimaryButton text="기업정보 보기" @click="onClickCorp()" />&nbsp;
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
      </div>
    </div>
    <div class="table-form">
      <table class="horizontal">
        <tr>
          <th>번호</th>
          <th>채용상태</th>
          <th>채용공고 제목</th>
          <th>마스터</th>
          <th>관심 카테고리</th>
          <th>면접 방식</th>
          <th>채용기간</th>
          <th>채용공고 등록일</th>
          <th>입사 지원 상태</th>
        </tr>
        <tr v-for="jobNotice in list">
          <td>{{ jobNotice.boardNo }}</td>
          <td>{{ $gbNameKR('JOB_NOTICE_STATE_GB', jobNotice.jobNoticeStateGb) }}</td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(jobNotice)">{{ jobNotice.jobName }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', jobNotice.createId)">{{ jobNotice.userName }}</span>
          </td>
          <td>{{ jobNotice.interests }}</td>
          <td>{{ $gbNameKR('INTV_GB', jobNotice.intvGb) }}</td>
          <td>{{ $yyyymmdd(jobNotice.recruitStartYmd) }} ~ {{ $yyyymmdd(jobNotice.recruitEndYmd) }}</td>
          <td>{{ $yyyymmddHHmmss(jobNotice.createDate) }}</td>
          <td>
            <span v-if="jobNotice.jobNoticeStateGb === '01'" class="gray">지원대기</span>
            <span v-else-if="jobNotice.applyStateGb === '01' || jobNotice.applyStateGb === '02' || jobNotice.applyStateGb === '03' || jobNotice.applyStateGb === '04' || jobNotice.applyStateGb === '05' || jobNotice.applyStateGb === '06'" class="gray">지원완료</span>
            <span v-else>-</span>
          </td>
        </tr>
      </table>
    </div>
    <div class="result-none lecture-none" v-if="list.length === 0">
      등록된 채용공고가 없습니다.
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
  <JobFairCorpInfoDetailModal ref="corpInfoModal"/> <!-- 기업정보 보기 -->
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
</template>

<style scoped>
</style>
